import { Controller } from "@hotwired/stimulus"
import i18n from "../javascripts/util/i18n"

export default class extends Controller {
  static targets = ["dialog", "content", "title"]

  connect() {
    this.onContentClick = this.onContentClick.bind(this)
    this.onContentChange = this.onContentChange.bind(this)
    this.contentTarget.addEventListener("click", this.onContentClick)
    this.contentTarget.addEventListener("change", this.onContentChange)
  }

  disconnect() {
    if (this.hasContentTarget) {
      this.contentTarget.removeEventListener("click", this.onContentClick)
      this.contentTarget.removeEventListener("change", this.onContentChange)
    }
  }

  onContentChange(event) {
    if (event.target.name === "partner_id") this.applyPartnerLocks(event.target.value)
  }

  // The boxes a partner decides (its settings, or its admin conditions) show its value, locked and marked, so what
  // is ticked is what the preview uses. Without a partner the user's own ticks come back.
  applyPartnerLocks(partnerId) {
    const form = this.contentTarget.querySelector("[data-test-preview-role='form']")
    if (!form) return

    let locks = {}
    try {
      locks = JSON.parse(form.dataset.testPreviewLocks || "{}")[partnerId] || {}
    } catch {
      locks = {}
    }

    form.querySelectorAll("[data-test-preview-condition]").forEach((box) => {
      const lock = locks[box.dataset.testPreviewCondition]
      const badge = box.closest("label")?.querySelector("[data-test-preview-lock]")

      if (lock) {
        if (box.dataset.userChecked === undefined) box.dataset.userChecked = box.checked ? "1" : "0"
        box.checked = Number(lock[0]) === 1
        box.disabled = true
        if (badge) {
          badge.textContent = i18n.t(lock[1] === "admin" ? "js.test_preview.locked_admin" : "js.test_preview.locked_partner")
          badge.classList.remove("hidden")
        }
      } else {
        if (box.dataset.userChecked !== undefined) {
          box.checked = box.dataset.userChecked === "1"
          delete box.dataset.userChecked
        }
        box.disabled = false
        badge?.classList.add("hidden")
      }
    })
  }

  async open(event) {
    event.preventDefault()

    const trigger = event.currentTarget
    const url = trigger?.dataset?.testPreviewDialogUrl
    const modalTitle = trigger?.dataset?.testPreviewModalTitle
    if (!url) {
      return
    }

    if (this.hasTitleTarget && modalTitle) {
      this.titleTarget.textContent = modalTitle
    }

    // Size the modal per trigger: the generate form is narrow, the test preview
    // needs its two-column width (default).
    if (this.hasDialogTarget) {
      const width = trigger?.dataset?.testPreviewModalWidth || "max-w-6xl"
      this.dialogTarget.classList.remove("max-w-lg", "max-w-2xl", "max-w-4xl", "max-w-6xl")
      this.dialogTarget.classList.add(width)
    }

    this.contentTarget.innerHTML = `<p class="text-sm text-gray-500 dark:text-gray-400">${i18n.t("js.test_preview.loading")}</p>`
    this.showDialog()

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" } })
      if (!response.ok) {
        throw new Error("Failed to load preview dialog")
      }

      this.contentTarget.innerHTML = await response.text()
    } catch (error) {
      this.contentTarget.innerHTML = `<p class="text-sm text-red-600 dark:text-red-400">${i18n.t("js.test_preview.load_failed")}</p>`
    }
  }

  close() {
    if (!this.hasDialogTarget) {
      return
    }

    if (this.dialogTarget.close) {
      this.dialogTarget.close()
    } else {
      this.dialogTarget.removeAttribute("open")
    }
  }

  showDialog() {
    if (!this.hasDialogTarget) {
      return
    }

    if (this.dialogTarget.showModal) {
      this.dialogTarget.showModal()
    } else {
      this.dialogTarget.setAttribute("open", "open")
    }
  }

  async onContentClick(event) {
    const submit = event.target.closest("[data-test-preview-role='submit']")
    if (!submit) {
      return
    }

    event.preventDefault()

    const form = this.contentTarget.querySelector("[data-test-preview-role='form']")
    const result = this.contentTarget.querySelector("[data-test-preview-role='result']")
    if (!form || !result) {
      return
    }

    submit.disabled = true
    result.innerHTML = `<p class="text-sm text-gray-500 dark:text-gray-400">${i18n.t("js.test_preview.generating")}</p>`

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" }
      })

      const payload = await response.json()
      if (!response.ok || payload.success !== true) {
        const msg = payload.msg || i18n.t("js.test_preview.failed")
        result.innerHTML = `<p class="text-sm text-red-600 dark:text-red-400">${msg}</p>`
        return
      }

      let warnings = ""
      if (Array.isArray(payload.warnings) && payload.warnings.length) {
        const warningLines = payload.warnings.map((warning) => this.escapeHtml(String(warning))).join("<br>")
        warnings = `<div class="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">${warningLines}</div>`
      }

      result.innerHTML = `${warnings}${payload.html || ""}`
    } catch (error) {
      result.innerHTML = `<p class="text-sm text-red-600 dark:text-red-400">${i18n.t("js.test_preview.failed")}</p>`
    } finally {
      submit.disabled = false
    }
  }

  escapeHtml(value) {
    return value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
